import type {
  CalendarClientNoteStatusResponse,
  CalendarEvent,
  CoinBalanceBreakdown,
  RealEstateContact,
  REST_schema,
  AddFacebookSourceRequest,
  AddFacebookSourceResponse,
  ImportFacebookSourcesRequest,
  ImportFacebookSourcesResponse,
  CheckFacebookSourceRequest,
  CheckFacebookSourceResponse,
  ConvertFacebookLeadRequest,
  FacebookAdminSourceDetailResponse,
  FacebookLeadStatus,
  FacebookRefreshStatus,
  GetFacebookAdminSourcesStatsResponse,
  GetFacebookLeadDetailResponse,
  GetFacebookLeadNotesResponse,
  GetFacebookLeadsRequest,
  GetFacebookLeadsResponse,
  GetFacebookPersonHistoryRequest,
  GetFacebookPersonHistoryResponse,
  GetFacebookSourcesResponse,
  StartFacebookRefreshResponse,
  UpdateFacebookSourceRequest,
  WhatsappCampaign,
  WhatsappConversation,
  WhatsappMessage,
  WhatsappOnboardingMode,
  WhatsappSuppressionReason,
  WhatsappTenantStatus,
  UserNote
} from './sharedSchema';

export type WebotServerContextProps = {
  getOrgAdminStats: (
    dateFrom: Date,
    dateEnd: Date,
    organizationId?: string
  ) => Promise<orgAdminStatsResponseSchema>;
  markNoSpamContacts: () => Promise<MarkNoSpamContactsResponse>;
  /** Server-side contacts list (Postgres mirror). See CrmContactsListRequest. */
  contactsList: (request: CrmContactsListRequest) => Promise<CrmContactsListResponse>;
  /** Cross-agent duplicate roll-up. Org admins only; 403 otherwise. */
  contactsLeadsBoard: (request: CrmContactsListRequest) => Promise<CrmLeadsBoardResponse>;
  /**
   * Who else in the agency holds these people. Seeded by ids from a page already served.
   * Never refuses — see `CrmContactHoldersResponse`. Prefer `useCrmHolders()`, which
   * batches one call per loaded page and caches per (org, contact).
   */
  contactsHolders: (request: CrmContactHoldersRequest) => Promise<CrmContactHoldersResponse>;
  /**
   * The caller's OWN rows for a list of phones (at most `CONTACTS_LOOKUP_MAX_IDS`).
   *
   * ⚠️ CALL `useContactsLookup()` / `useContactLookup()` INSTEAD, from UI code. They share one
   * module store and coalesce every phone asked about in a tick into chunked requests;
   * calling this per row is one HTTP request per name on the screen.
   */
  contactsLookup: (contactIds: string[]) => Promise<ContactsLookupResponse>;
  /**
   * The caller's OWN rows that are the seller / landlord of any of these listings (at most
   * `CONTACTS_LISTING_SELLERS_MAX_IDS`) — asked by the listing, not the ad's phone.
   *
   * ⚠️ CALL `useListingSellerContacts()` INSTEAD, from UI code: one module store, every listing
   * asked about in a tick in one request.
   */
  contactsListingSellers: (listingIds: string[]) => Promise<ContactsListingSellersResponse>;
  /** Which of these phones the caller already has — an import's comparison. See `ContactPresence`. */
  contactsPresence: (contactIds: string[]) => Promise<ContactsPresenceResponse>;
  /**
   * Which book the caller's contacts live in — their own, or a pooled agency's and its mode
   * (plan D4). Zero I/O server-side, so asking on every session is fine.
   *
   * ⚠️ CALL `useContactBook()` INSTEAD of this, from UI code. One module store holds the
   * answer and every document reader, signal listener and write keys off it.
   */
  contactsBook: () => Promise<REST_schema.CrmContactBookResponse>;
  /**
   * "My contacts" inside a POOLED agency: put a member on a person's holder list, or take
   * them off it. Self, or an admin for any member. Refused (400) on a personal book.
   *
   * ⚠️ CALL `useContactWrites().addContactHolder` / `removeContactHolder` INSTEAD — they
   * also tell the local stores the list changed.
   */
  contactsHoldersAdd: (
    request: REST_schema.CrmContactHolderChangeRequest
  ) => Promise<REST_schema.CrmContactHolderAddResponse>;
  contactsHoldersRemove: (
    request: REST_schema.CrmContactHolderChangeRequest
  ) => Promise<REST_schema.CrmContactHolderRemoveResponse>;
  /** The home page's contact tiles. See `ContactsSummaryResponse`. */
  contactsSummary: () => Promise<ContactsSummaryResponse>;
  /**
   * The caller's tag vocabulary: every distinct tag on the live contacts of their OWN book and
   * how many carry it, most used first — at most 500, the server's cap (`limit` only lowers
   * it). The tag field's suggestions. Nothing is stored between two calls, so a tag no contact
   * carries any more is simply not in the next answer.
   *
   * ⚠️ CALL `useContactTagSuggestions()` INSTEAD, from UI code — it asks once per opening of
   * the field and drops an answer whose opening has ended.
   */
  contactsTags: (request?: REST_schema.CrmContactTagsRequest) => Promise<REST_schema.CrmContactTagsResponse>;
  /**
   * Who the agency designated to handle these people — the מתווך מטפל badge.
   *
   * ⚠️ A SEPARATE CALL FROM `contactsHolders`, ON PURPOSE. That one is gated on `browse`,
   * which is false for a default member, so folding the designation into it would publish
   * it to admins and to nobody else — the opposite of the point. This one is authorized on
   * plain org membership. Prefer `useCrmAssignments()` / `useContactHandler()`, which batch
   * per loaded page and share one module cache across every surface on the screen.
   */
  contactsAssignments: (
    request: REST_schema.CrmContactAssignmentsRequest
  ) => Promise<REST_schema.CrmContactAssignmentsResponse>;
  /** Set or clear the מתווך מטפל, copying the contact into the assignee's book if needed. */
  crmAssignContactHandler: (
    request: REST_schema.CrmAssignContactRequest
  ) => Promise<REST_schema.CrmAssignContactResponse>;
  /**
   * The bulk form: ONE assignee for up to 200 enumerated contacts, each reported on its own
   * (`results[i].status`). Per-contact refusals come back as `failed` entries, never as a
   * rejected promise — only a request-level refusal (no agency, ambiguous org, bad body) throws.
   */
  crmAssignContactHandlersBulk: (
    request: REST_schema.CrmAssignContactsBulkRequest
  ) => Promise<REST_schema.CrmAssignContactsBulkResponse>;
  /**
   * Buyer-side matches counts (`favorite-listings-of-contact`) for a page of served
   * rows. Never refuses — `enabled: false` means "render nothing". Prefer
   * `useCrmMatchesCounts()`, which batches one call per loaded page and caches per
   * (owner, contact) pair.
   */
  contactsMatchesCounts: (
    request: REST_schema.CrmMatchesCountsRequest
  ) => Promise<REST_schema.CrmMatchesCountsResponse>;
  /**
   * Lean summaries of the rows' linked SELLER listings (`sellingListingIds` /
   * `rentingOutListingIds`). Never refuses — `enabled: false` means "render nothing".
   * Prefer `useCrmListingLinks()`, which batches per page and caches per listing id.
   */
  contactsListingLinks: (
    request: REST_schema.CrmListingLinksRequest
  ) => Promise<REST_schema.CrmListingLinksResponse>;
  /**
   * What each office colleague sent this customer: their minisites for the phone, the listings on
   * each and what the customer did with them. `enabled: false` = the caller is in no office.
   * Prefer `useOfficeMinisites()`.
   */
  contactsOfficeMinisites: (
    request: REST_schema.CrmOfficeMinisitesRequest
  ) => Promise<REST_schema.CrmOfficeMinisitesResponse>;
  /**
   * One contact's whole document and its mirror version, by exact id or by every spelling
   * of a phone (`candidates`; the first that exists wins). `found: false` is an answer.
   * Prefer `useContactDocument()` / `useContactByPhone()`, which share the document store,
   * dedupe requests and refetch on the contact's signal.
   */
  contactsGet: (request: REST_schema.CrmContactGetRequest) => Promise<REST_schema.CrmContactGetResponse>;
  /**
   * The documents behind a list of ids in one book — at most 5,000, refused above, never
   * trimmed. `missing` is an answer. The old contacts page's feed catch-up.
   */
  contactsGetMany: (
    request: REST_schema.CrmContactGetManyRequest
  ) => Promise<REST_schema.CrmContactGetManyResponse>;
  /**
   * Updates one contact the caller can see. Scoped, whitelisted and audited server-side;
   * returns the PERSISTED row. Does not create — a missing contact is a 404.
   */
  contactsUpdate: (request: CrmContactUpdateRequest) => Promise<CrmContactUpdateResponse>;
  /** Creates one contact. 409 when the number is already in that book. */
  contactsCreate: (request: CrmContactCreateRequest) => Promise<CrmContactCreateResponse>;
  /** The file import's chunked upsert. Rows fail individually — read `results`. */
  contactsBulkCreate: (request: CrmContactsBulkCreateRequest) => Promise<CrmContactsBulkCreateResponse>;
  /**
   * Changes a contact's PHONE NUMBER — a server-side RE-KEY: the doc id IS the phone,
   * so the server copies the document to the new id, ports every satellite (notes,
   * calendar, signatures, calls, favorites, …) and hard-deletes the old doc LAST.
   * Own book only. 409 `CRM_CONFLICT` when anything (live or soft-deleted) already
   * sits at the target phone; on a 5xx the port may be PARTIAL and re-sending the
   * SAME request is safe — the server resumes from where it stopped.
   */
  contactsChangePhone: (
    request: REST_schema.ChangeContactPhoneRequest
  ) => Promise<REST_schema.ChangeContactPhoneResponse>;
  /** Records ONE bulk-send outcome server-side — see `CrmRecordBulkMessageRequest`. */
  contactsRecordBulkMessage: (
    request: CrmRecordBulkMessageRequest
  ) => Promise<CrmRecordBulkMessageResponse>;
  /**
   * Soft-deletes a SELECTION. Gated on the agency's `delete` permission, capped by
   * `maxBulkDelete`, audited, and recoverable — unlike the browser-side hard delete it
   * replaces. Refuses the whole request on a permission or drift failure; a contact that
   * was already gone comes back in `skipped`, which is a fact about the data, not a failure.
   */
  contactsDelete: (request: CrmContactsDeleteRequest) => Promise<CrmContactsDeleteResponse>;
  /**
   * Applies ONE patch to a whole selection. Same field whitelist as `contactsUpdate` —
   * one vocabulary, not two — and the patch REPLACES rather than merges. See
   * {@link CrmContactsBulkUpdateRequest} for the empty-value rule that keeps that survivable.
   */
  contactsBulkUpdate: (
    request: CrmContactsBulkUpdateRequest
  ) => Promise<CrmContactsBulkUpdateResponse>;
  /**
   * Exports a selection (or the current view) as CSV.
   *
   * NOT routed through `sendWebotRequest`: that helper throws on any non-JSON body, and
   * this endpoint answers `text/csv`. It has its own fetch for that reason alone — see
   * the implementation, which also reads the two exposed response headers.
   */
  contactsExportCsv: (request: CrmContactsExportRequest) => Promise<CrmContactsExportFile>;
  /**
   * The agency surface, in one call — for BOTH audiences.
   *
   * A plain member is NOT refused: they get `isAdmin: false`, their OWN resolved
   * permissions and `members: null`. Only "you are in no agency" / "you are not in
   * THAT agency" answer 403.
   *
   * `orgId` may be omitted when the caller belongs to exactly one org — otherwise
   * the server answers 400 CRM_SCOPE_AMBIGUOUS and the screen shows an org picker.
   */
  crmOrgOverview: (orgId?: string) => Promise<CrmOrgOverviewResponse>;
  /** Saves the agency's two caps. Returns the settings AFTER the write. Admins only. */
  crmOrgUpdateSettings: (request: CrmOrgSettingsUpdateRequest) => Promise<CrmOrgSettings>;
  /** Saves one member's permissions (partial). Returns the resolved row. Admins only. */
  crmOrgUpdateMember: (request: CrmOrgMemberUpdateRequest) => Promise<CrmOrgMemberView>;
  /**
   * The SAME change on several members in ONE request — a partial patch, or `reset: true`
   * to put each of them back on their own role default. All-or-nothing on the server: one
   * target who is no longer a member 403s the whole request with nothing written. Returns
   * the resolved rows of every target. Admins only.
   */
  crmOrgUpdateMembersBulk: (
    request: CrmOrgMembersBulkUpdateRequest
  ) => Promise<CrmOrgMembersBulkUpdateResponse>;
  /**
   * Makes a member an admin, or an admin a member — the one call that writes the roster.
   * Admins only; the server refuses to demote the LAST admin (400 with the reason) and
   * keeps every admin inside `users[]`. Returns the member's row as the overview now
   * serves it.
   */
  crmOrgUpdateMemberRole: (request: CrmOrgMemberRoleUpdateRequest) => Promise<CrmOrgMemberView>;
  /**
   * The pooled-book switch (plan D4, the admin's pool card). The shapes are the server's
   * `REST_schema.CrmOrgPool*` (mirrored in `sharedSchema.ts`). Admin-only, and behind the
   * server's code allowlist of eligible agencies — a 403 is the normal answer for most of the
   * fleet. Prefer `useCrmOrgPool()`, which owns the polling and the state machine.
   *
   *  - `crmOrgPool`           the agency's state: mode, the refusals that stand today, the
   *                           last run and how far it is
   *  - `crmOrgPoolPreview`    read-only: the overlap counts, the plan, example conflicts, the
   *                           refusals, and a confirm token bound to the numbers and the day
   *  - `crmOrgPoolMigrate`    runs the merge inside the request; a dropped request resumes
   *                           from the cursor on the next call, without a token
   *  - `crmOrgPoolMembersSync` a POOLED office brought in line with its roster: whoever
   *                           joined since has their book folded into the office's, whoever
   *                           left is taken off the lists. Busy like the merge while it folds
   *  - `crmOrgPoolProgress`   the office's records and the rows still in personal books —
   *                           polled while a switch is on
   *  - `crmOrgPoolReverse`    the way back: every copy restored, the org back to `personal`
   */
  crmOrgPool: (orgId?: string) => Promise<REST_schema.CrmOrgPoolStateResponse>;
  crmOrgPoolPreview: (request: REST_schema.CrmOrgPoolPreviewRequest) => Promise<REST_schema.CrmOrgPoolPreviewResponse>;
  crmOrgPoolMigrate: (request: REST_schema.CrmOrgPoolMigrateRequest) => Promise<REST_schema.CrmOrgPoolMigrateResponse>;
  crmOrgPoolMembersSync: (
    request: REST_schema.CrmOrgPoolSyncMembersRequest
  ) => Promise<REST_schema.CrmOrgPoolSyncMembersResponse>;
  crmOrgPoolProgress: (orgId?: string) => Promise<REST_schema.CrmOrgPoolProgressResponse>;
  crmOrgPoolReverse: (request: REST_schema.CrmOrgPoolReverseRequest) => Promise<REST_schema.CrmOrgPoolReverseResponse>;
  /**
   * The caller's colleagues: name, avatar, email, admin-or-not. Open to EVERY member.
   *
   * Never 403s and never 400s — a caller in no agency gets a one-row directory holding
   * themselves, a caller in several gets the union plus `orgs`. Prefer the cached
   * `useOrgDirectory()` hook over calling this directly: the whole point is one fetch per
   * session, shared by every screen.
   */
  orgDirectory: (orgId?: string) => Promise<OrgDirectoryResponse>;
  getBuyersSuggestionsForListingAndRealtor: (
    listingId: string
  ) => Promise<REST_schema.RelevantContactsForListingResponse>;
  /**
   * The LAST computed matches for (caller, listing), from the server's snapshot — `snapshot: null`
   * when there is none or it is stale. Own rows are a projection: never feed them to a bulk send.
   */
  getLastBuyersSuggestionsForListing: (
    listingId: string
  ) => Promise<REST_schema.LastRelevantContactsForListingResponse>;
  /** A WhatsApp listing card's own page: the listings of that one card (GET /api/v1/cards/page/:cardId). */
  getListingCardPage: (cardId: string) => Promise<ListingCardPage>;
  getListingOrgActivity: (listingIds: string[]) => Promise<REST_schema.ListingOrgActivityResponse>;
  // --- Listing claim / marketplace-source links ---
  claimMarketListing: (
    sourceListingId: string,
    seller?: REST_schema.ListingSellerInput
  ) => Promise<REST_schema.ClaimListingResponse>;
  linkListingSource: (
    listingId: string,
    sourceListingId: string
  ) => Promise<REST_schema.LinkListingSourceResponse>;
  unlinkListingSource: (
    listingId: string,
    sourceListingId: string
  ) => Promise<REST_schema.LinkListingSourceResponse>;
  linkListingContact: (
    listingId: string,
    contactId: string | null,
    contactIndex?: number
  ) => Promise<{ listingId: string }>;
  getListingSourceCandidates: (
    request: REST_schema.ListingSourceCandidatesRequest
  ) => Promise<REST_schema.ListingSourceCandidatesResponse>;
  webotCalendarOnEventCreated: (event: CalendarEvent) => Promise<void>;
  webotCalendarOnEventUpdated: (event: CalendarEvent, timesChanged: boolean) => Promise<void>;
  webotCalendarOnEventDeleted: (event: CalendarEvent) => Promise<void>;
  /** Office tasks — see `WebotServerContext`. The server is the only writer across calendars. */
  calendarTaskAssign: (
    request: REST_schema.CalendarTaskAssignRequest
  ) => Promise<REST_schema.CalendarTaskAssignResponse>;
  calendarTaskUpdate: (
    request: REST_schema.CalendarTaskUpdateRequest
  ) => Promise<REST_schema.CalendarTaskUpdateResponse>;
  calendarTaskCancel: (request: REST_schema.CalendarTaskCancelRequest) => Promise<void>;
  /**
   * May the calendar form OFFER the "message to invitees" input (`CalendarEvent.clientNote`)?
   * The server answers `enabled: true` only once every WhatsApp template that carries the note
   * is approved. Rejects on any transport failure, and a rejection is NOT "off". Prefer
   * `useCalendarClientNoteEnabled()`, which caches the answer and keeps the two apart.
   */
  getCalendarClientNoteStatus: () => Promise<CalendarClientNoteStatusResponse>;
  // --- Google Calendar two-way sync ---
  googleCalendarGetStatus: () => Promise<REST_schema.GoogleCalendarStatusResponse>;
  googleCalendarStartConnect: (
    returnPath?: string
  ) => Promise<REST_schema.GoogleCalendarConnectResponse>;
  googleCalendarDisconnect: () => Promise<REST_schema.GoogleCalendarStatusResponse>;
  googleCalendarSyncNow: () => Promise<REST_schema.GoogleCalendarStatusResponse>;
  googleCalendarListCalendars: () => Promise<REST_schema.GoogleCalendarListResponse>;
  googleCalendarUpdateSettings: (
    updates: REST_schema.GoogleCalendarUpdateSettingsRequest
  ) => Promise<REST_schema.GoogleCalendarStatusResponse>;
  getWebotRealtorInfo: (realtorId: string) => Promise<REST_schema.PublicRealtorInfoResponse | null>;
  publishListingToMadlan: (
    processedListingId: string,
    confirmedAddress?: string,
    locationResolution?: REST_schema.MadlanLocationResolutionType
  ) => Promise<REST_schema.MadlanPublishListingResponse>;
  madlanAutocomplete: (
    request: REST_schema.MadlanAutocompleteRequest
  ) => Promise<REST_schema.MadlanAutocompleteResponse>;
  contactSearch: (searchTerm: string) => Promise<REST_schema.ContactSearchResponseSchema>;
  getContactDetails: (
    phoneNumberInternational: string,
    dataSources?: REST_schema.ContactDetailsDataSource[]
  ) => Promise<REST_schema.ContactDetailsResponseSchema>;
  getContactSmartDefaults: () => Promise<REST_schema.GetContactSmartDefaultsResponse>;
  getUpdateContactWebhookForCustomer: () => Promise<REST_schema.GetUpdateContactWebhookForCustomerResponse>;
  rotateUpdateContactWebhookKey: () => Promise<REST_schema.RotateUpdateContactWebhookKeyResponse>;
  generateCreative: (
    request: REST_schema.GenerateCreativeRequest
  ) => Promise<REST_schema.GenerateCreativeResponse>;
  generateMakeovers: (
    request: REST_schema.GenerateMakeoversRequest
  ) => Promise<REST_schema.GenerateMakeoversResponse>;
  setMakeoverShowcase: (
    request: REST_schema.SetMakeoverShowcaseRequest
  ) => Promise<REST_schema.SetMakeoverShowcaseResponse>;
  deleteMakeover: (
    request: REST_schema.DeleteMakeoverRequest
  ) => Promise<REST_schema.DeleteMakeoverResponse>;
  removeListingWatermarks: (
    request: REST_schema.RemoveListingWatermarksRequest
  ) => Promise<REST_schema.RemoveListingWatermarksResponse>;
  getWebotListingMakeovers: (
    realtorMail: string,
    listingIds: string[]
  ) => Promise<REST_schema.PublicListingMakeoversResponse>;
  saveListingPresentation: (
    request: REST_schema.SaveListingPresentationRequest
  ) => Promise<REST_schema.SaveListingPresentationResponse>;
  getListingPresentation: (
    listingId: string
  ) => Promise<REST_schema.GetListingPresentationResponse>;
  getMcpConnectionInfo: () => Promise<REST_schema.McpConnectionInfoResponse>;
  // Agency-ambassador offer (personal upsell campaign, until 2026-12-01)
  getAgencyOffer: () => Promise<REST_schema.AgencyOfferResponse>;
  registerAgencyOfferInterest: (
    request: REST_schema.AgencyOfferInterestRequest
  ) => Promise<REST_schema.AgencyOfferInterestResponse>;
  // Centralized admin
  adminSearchUsers: (search: string) => Promise<REST_schema.AdminUserSearchResponse>;
  adminSearchOrgs: (search: string) => Promise<REST_schema.AdminOrgSearchResponse>;
  // Admin users screen (user management)
  adminGetUserProfile: (userId: string) => Promise<REST_schema.AdminUserProfileResponse>;
  adminGetUserStats: (userId: string) => Promise<REST_schema.AdminUserStatsResponse>;
  adminUpdateUser: (
    request: REST_schema.AdminUpdateUserRequest
  ) => Promise<REST_schema.AdminUpdateUserResponse>;
  // DB explorer (owner-only Postgres console — gated on DB_EXPLORER_ALLOWED_EMAILS)
  dbExplorerInfo: () => Promise<REST_schema.DbExplorerInfoResponse>;
  dbExplorerListTables: (
    db: REST_schema.DbExplorerDbKey
  ) => Promise<REST_schema.DbExplorerTablesResponse>;
  dbExplorerTableSchema: (
    db: REST_schema.DbExplorerDbKey,
    table: string
  ) => Promise<REST_schema.DbExplorerTableSchemaResponse>;
  dbExplorerBrowseRows: (
    db: REST_schema.DbExplorerDbKey,
    table: string,
    request: REST_schema.DbExplorerBrowseRequest
  ) => Promise<REST_schema.DbExplorerBrowseResponse>;
  dbExplorerInsertRow: (
    db: REST_schema.DbExplorerDbKey,
    table: string,
    request: REST_schema.DbExplorerInsertRequest
  ) => Promise<REST_schema.DbExplorerMutationResponse>;
  dbExplorerUpdateRow: (
    db: REST_schema.DbExplorerDbKey,
    table: string,
    request: REST_schema.DbExplorerUpdateRequest
  ) => Promise<REST_schema.DbExplorerMutationResponse>;
  dbExplorerDeleteRow: (
    db: REST_schema.DbExplorerDbKey,
    table: string,
    request: REST_schema.DbExplorerDeleteRequest
  ) => Promise<REST_schema.DbExplorerMutationResponse>;
  dbExplorerRunSql: (
    db: REST_schema.DbExplorerDbKey,
    request: REST_schema.DbExplorerSqlRequest
  ) => Promise<REST_schema.DbExplorerSqlResponse>;
  // Sales map (מפת משרדים, owner-only — gated on SALES_MAP_ALLOWED_EMAILS) + live GoHighLevel panel
  salesMapGetSnapshot: () => Promise<REST_schema.SalesMapSnapshot>;
  salesMapGhlContext: () => Promise<REST_schema.SalesMapGhlContextResponse>;
  salesMapGhlLinks: () => Promise<REST_schema.SalesMapGhlLinksResponse>;
  salesMapGhlLink: (request: REST_schema.SalesMapGhlLinkRequest) => Promise<{ ok: true }>;
  salesMapGhlUnlink: (request: REST_schema.SalesMapGhlLinkRequest) => Promise<{ ok: true }>;
  salesMapGhlLookup: (request: REST_schema.SalesMapGhlLookupRequest) => Promise<REST_schema.SalesMapGhlLookupResponse>;
  salesMapGhlContact: (contactId: string) => Promise<REST_schema.SalesMapGhlContactResponse>;
  salesMapGhlAddNote: (
    contactId: string,
    request: REST_schema.SalesMapGhlAddNoteRequest
  ) => Promise<REST_schema.SalesMapGhlAddNoteResponse>;
  salesMapGhlCreateContact: (
    request: REST_schema.SalesMapGhlCreateContactRequest
  ) => Promise<REST_schema.SalesMapGhlCreateContactResponse>;
  salesMapGhlSetStage: (
    contactId: string,
    request: REST_schema.SalesMapGhlSetStageRequest
  ) => Promise<REST_schema.SalesMapGhlSetStageResponse>;
  salesMapGhlAssign: (
    contactId: string,
    request: REST_schema.SalesMapGhlAssignRequest
  ) => Promise<REST_schema.SalesMapGhlAssignResponse>;
  salesMapGhlTags: (
    contactId: string,
    request: REST_schema.SalesMapGhlTagsRequest
  ) => Promise<REST_schema.SalesMapGhlTagsResponse>;
  // Sales training portal (owner-only — SALES_TRAINING_ALLOWED_EMAILS)
  salesTrainingGetManifest: () => Promise<REST_schema.SalesTrainingManifest>;
  salesTrainingGetPage: () => Promise<string>;
  salesTrainingGetAudio: (name: string) => Promise<ArrayBuffer>;
  // Sales onboarding portal v2 (the owners + the readers they add from the page)
  salesTrainingV2GetAccess: () => Promise<REST_schema.SalesTrainingV2Access>;
  salesTrainingV2GetPage: () => Promise<string>;
  salesTrainingV2ListViewers: () => Promise<REST_schema.SalesTrainingV2ViewersResponse>;
  salesTrainingV2AddViewer: (email: string) => Promise<REST_schema.SalesTrainingV2ViewersResponse>;
  salesTrainingV2RemoveViewer: (email: string) => Promise<REST_schema.SalesTrainingV2ViewersResponse>;
  // Sales recruiting screening page (owner-only — SALES_RECRUITING_ADMIN_EMAILS)
  salesRecruitingList: () => Promise<REST_schema.SalesRecruitingListResponse>;
  salesRecruitingSetReview: (
    applicantId: string,
    status: REST_schema.SalesRecruitingReviewStatus | null
  ) => Promise<REST_schema.SalesRecruitingApplicantResponse>;
  salesRecruitingAddNote: (
    applicantId: string,
    text: string
  ) => Promise<REST_schema.SalesRecruitingApplicantResponse>;
  salesRecruitingDeleteNote: (
    applicantId: string,
    noteId: string
  ) => Promise<REST_schema.SalesRecruitingApplicantResponse>;
  salesRecruitingAction: (
    applicantId: string,
    action: "archive" | "unarchive" | "send-intro" | "send-link" | "give-back-intro"
  ) => Promise<REST_schema.SalesRecruitingApplicantResponse>;
  salesRecruitingSetAutomation: (enabled: boolean) => Promise<REST_schema.SalesRecruitingAutomationResponse>;
  salesRecruitingRunIntake: () => Promise<REST_schema.SalesRecruitingAutomationResponse>;
  // Location autocomplete (canonical registry; replaces Algolia suggestions)
  locationsAutocomplete: (
    query: string,
    opts?: {
      resultType?: REST_schema.LocationSuggestionLevel | 'any';
      includeEmpty?: boolean;
      includeCoords?: boolean;
      cityCode?: number;
      limit?: number;
      /**
       * Ask the server to expand multi-neighborhood streets into one street
       * suggestion PER neighborhood (each carrying `neighborhood`). Older server
       * deploys ignore the param and return plain street suggestions — callers
       * must treat `neighborhood` on a street suggestion as optional.
       */
      expandStreetNeighborhoods?: boolean;
    }
  ) => Promise<REST_schema.LocationsAutocompleteResponse>;
  /**
   * GET /api/v1/locations/street-neighborhood — corpus-based neighborhood
   * inference for a (city, street) pair. Used to auto-fill the neighborhood
   * after a street pick; `inference` is null whenever the answer would be a
   * guess (unresolved street, sparse data, street spans neighborhoods).
   */
  locationsStreetNeighborhood: (
    city: string,
    street: string
  ) => Promise<StreetNeighborhoodResponse>;
  // Canonical locations admin (registry explorer + corrections)
  adminLocationsOverview: () => Promise<REST_schema.AdminLocationOverviewResponse>;
  adminLocationsListCities: (
    q?: string,
    limit?: number
  ) => Promise<REST_schema.AdminLocationCitiesResponse>;
  adminLocationsCityDetail: (
    cityCode: number
  ) => Promise<REST_schema.AdminLocationCityDetailResponse>;
  adminLocationsGetLocation: (
    locationId: string
  ) => Promise<REST_schema.AdminLocationDetailResponse>;
  adminLocationsSearch: (
    q: string,
    level?: REST_schema.LocationLevel | 'any',
    cityCode?: number
  ) => Promise<REST_schema.AdminLocationSearchResponse>;
  adminLocationsResolvePreview: (
    request: REST_schema.AdminLocationResolvePreviewRequest
  ) => Promise<REST_schema.AdminLocationResolvePreviewResponse>;
  adminLocationsIssues: () => Promise<REST_schema.AdminLocationIssuesResponse>;
  adminLocationsLookup: (
    q: string,
    source?: string,
    limit?: number
  ) => Promise<REST_schema.AdminLocationLookupResponse>;
  adminLocationsSourceMappings: (
    cityCode: number,
    source?: string,
    limit?: number
  ) => Promise<REST_schema.AdminLocationSourceMappingsResponse>;
  adminLocationsSampleListings: (
    locationId: string,
    limit?: number,
    source?: string
  ) => Promise<REST_schema.AdminLocationListingsResponse>;
  adminLocationsListOverrides: () => Promise<REST_schema.AdminLocationOverridesResponse>;
  adminLocationsCreateOverride: (
    request: REST_schema.CreateLocationOverrideRequest
  ) => Promise<REST_schema.CreateLocationOverrideResponse>;
  adminLocationsDeleteOverride: (id: string) => Promise<{ ok: boolean }>;
  // Unresolved queue / AI proposals / resolution provenance
  adminLocationsUnresolved: (
    city?: string,
    origin?: string,
    limit?: number
  ) => Promise<REST_schema.AdminUnresolvedQueueResponse>;
  adminLocationsUnresolvedDetail: (
    id: string
  ) => Promise<REST_schema.AdminUnresolvedDetailResponse>;
  adminLocationsPropose: (ids: string[]) => Promise<REST_schema.AdminLocationProposeResponse>;
  adminLocationsListProposals: (
    status?: string
  ) => Promise<REST_schema.AdminLocationProposalsResponse>;
  adminLocationsApplyProposal: (id: string) => Promise<REST_schema.CreateLocationOverrideResponse>;
  adminLocationsDismissProposal: (id: string) => Promise<{ ok: boolean }>;
  adminLocationsProvenance: (
    city?: string,
    origin?: string
  ) => Promise<REST_schema.AdminLocationProvenanceResponse>;
  adminLocationsProvenanceListings: (filters: {
    method?: string;
    resolvedBy?: string;
    overrideId?: string;
    flag?: string;
    city?: string;
    origin?: string;
    limit?: number;
  }) => Promise<REST_schema.AdminLocationProvenanceListingsResponse>;
  // Coins
  getMyCoinBalance: () => Promise<CoinBalanceBreakdown>;
  adminGetUserCoins: (userId: string) => Promise<REST_schema.AdminUserCoinsResponse>;
  adminGetOrgCoins: (organizationId: string) => Promise<REST_schema.AdminOrgCoinsResponse>;
  adminGrantCoinPack: (
    request: REST_schema.AdminGrantCoinsRequest
  ) => Promise<REST_schema.AdminCoinsMutationResponse>;
  adminAdjustCoinPack: (
    request: REST_schema.AdminAdjustCoinsRequest
  ) => Promise<REST_schema.AdminCoinsMutationResponse>;
  // Facebook leads
  getFacebookSources: () => Promise<GetFacebookSourcesResponse>;
  checkFacebookSource: (
    request: CheckFacebookSourceRequest
  ) => Promise<CheckFacebookSourceResponse>;
  addFacebookSource: (request: AddFacebookSourceRequest) => Promise<AddFacebookSourceResponse>;
  /** "ייבוא מהקבוצות שלי": groups picked from the list the extension read in this browser (up to 50 a call). */
  importFacebookSources: (request: ImportFacebookSourcesRequest) => Promise<ImportFacebookSourcesResponse>;
  updateFacebookSource: (id: string, patch: UpdateFacebookSourceRequest) => Promise<void>;
  removeFacebookSource: (id: string) => Promise<void>;
  getFacebookLeads: (request: GetFacebookLeadsRequest) => Promise<GetFacebookLeadsResponse>;
  getFacebookLeadDetail: (leadId: string) => Promise<GetFacebookLeadDetailResponse>;
  setFacebookLeadStatus: (leadId: string, status: FacebookLeadStatus) => Promise<void>;
  convertFacebookLead: (leadId: string, request: ConvertFacebookLeadRequest) => Promise<void>;
  getFacebookLeadNotes: (leadId: string) => Promise<GetFacebookLeadNotesResponse>;
  addFacebookLeadNote: (
    leadId: string,
    note: string,
    organizationIds?: string[]
  ) => Promise<{ note: UserNote }>;
  getFacebookPersonHistory: (
    request: GetFacebookPersonHistoryRequest
  ) => Promise<GetFacebookPersonHistoryResponse>;
  // On-demand refresh: HTTP 200 for every business outcome, a refusal included.
  getFacebookRefreshStatus: () => Promise<FacebookRefreshStatus>;
  startFacebookRefresh: () => Promise<StartFacebookRefreshResponse>;
  // Facebook sources admin (webot-admin only)
  getFacebookAdminSourcesStats: () => Promise<GetFacebookAdminSourcesStatsResponse>;
  getFacebookAdminSourceDetail: (id: string) => Promise<FacebookAdminSourceDetailResponse>;
  setFacebookSourceActive: (id: string, active: boolean) => Promise<void>;
  rescrapeFacebookSource: (id: string) => Promise<void>;
  adminRemoveFacebookSource: (id: string) => Promise<void>;
  // Official WhatsApp (Meta Cloud API, per-customer)
  whatsappGetStatus: () => Promise<WhatsappStatusResponse>;
  whatsappBeginConnect: (request: {
    businessName: string;
    mode?: WhatsappOnboardingMode;
  }) => Promise<WhatsappConnectConfig>;
  whatsappCompleteConnect: (request: {
    code: string;
    wabaId?: string;
    phoneNumberId?: string;
  }) => Promise<{ status: string; displayPhoneNumber?: string; verifiedName?: string }>;
  /** Telemetry only — see {@link WhatsappConnectDiagnostics}. Callers must not let it fail the screen. */
  whatsappReportConnectDiagnostics: (request: WhatsappConnectDiagnostics) => Promise<{ ok: boolean }>;
  whatsappDisconnect: (request?: { releaseAtMeta?: boolean }) => Promise<{
    ok: boolean;
    releasedAtMeta?: boolean;
    unsubscribedApp?: boolean;
    deregisteredNumber?: boolean;
    warning?: string;
  }>;
  whatsappCheckStatus: () => Promise<REST_schema.WhatsappStatusCheckResponse>;
  whatsappGetConversations: (
    numberKey: string
  ) => Promise<{ conversations: WhatsappConversationView[] }>;
  whatsappGetMessages: (conversationId: string) => Promise<{ messages: WhatsappMessageView[] }>;
  whatsappMarkRead: (conversationId: string) => Promise<{ ok: boolean }>;
  whatsappSendMessage: (
    conversationId: string,
    text: string
  ) => Promise<{ wamid: string; messageId: string }>;
  whatsappListNumberTemplates: (
    numberKey: string
  ) => Promise<{ templates: REST_schema.WhatsappNumberTemplateView[] }>;
  whatsappSendTemplate: (
    conversationId: string,
    request: { templateName: string; language: string; params?: string[] | Record<string, string> }
  ) => Promise<{ wamid: string; messageId: string }>;
  // Smart assistant — the programmable, tool-less auto-responder on a number.
  /** Dry-runs one turn against a prompt. Writes nothing, sends nothing. */
  whatsappTestAssistant: (
    numberKey: string,
    request: REST_schema.WhatsappAssistantTestRequest
  ) => Promise<REST_schema.WhatsappAssistantTestResponse>;
  /** Updates the number's assistant config. Server-whitelisted fields only. */
  whatsappUpdateAssistant: (
    numberKey: string,
    request: REST_schema.WhatsappAssistantUpdateRequest
  ) => Promise<REST_schema.WhatsappAssistantUpdateResponse>;
  // Sharing a number with the office (owner / org admin; see the share dialog).
  /** Who sees the number, and — for the owner or an org admin — who else could. */
  whatsappGetNumberSharing: (numberKey: string) => Promise<REST_schema.WhatsappNumberSharingView>;
  /** Sets the membership among the office roster. The server refuses anyone outside it. */
  whatsappUpdateNumberSharing: (
    numberKey: string,
    request: REST_schema.WhatsappNumberSharingUpdateRequest
  ) => Promise<REST_schema.WhatsappNumberSharingUpdateResponse>;
  // Templates management
  whatsappGetTemplates: (
    sync?: boolean,
    numberKey?: string
  ) => Promise<REST_schema.WhatsappListTemplatesResponse>;
  whatsappValidateTemplate: (
    request: REST_schema.WhatsappCreateTemplateRequest
  ) => Promise<REST_schema.WhatsappValidateTemplateResponse>;
  whatsappCreateTemplate: (
    request: REST_schema.WhatsappCreateTemplateRequest
  ) => Promise<{ template: REST_schema.WhatsappTemplateView }>;
  whatsappUploadTemplateMedia: (request: {
    dataBase64: string;
    fileName: string;
    mimeType: string;
  }) => Promise<REST_schema.WhatsappTemplateMediaResponse>;
  whatsappCreateStarterPack: (numberKey?: string) => Promise<REST_schema.WhatsappStarterPackResponse>;
  whatsappTemplateAction: (
    templateId: string,
    action: 'edit' | 'unpause' | 'sync' | 'delete',
    request?: REST_schema.WhatsappEditTemplateRequest
  ) => Promise<unknown>;
  /** `POST /whatsapp/templates/:id/sources` — bindings, fallbacks and the header source; never Meta. */
  whatsappTemplateSources: (templateId: string, request: REST_schema.WhatsappTemplateSourcesRequest) => Promise<unknown>;
  whatsappTemplateStats: (
    request: REST_schema.WhatsappTemplateStatsRequest
  ) => Promise<REST_schema.WhatsappTemplateStatsResponse>;
  whatsappSentMessagesLog: (
    request: REST_schema.WhatsappSentLogRequest
  ) => Promise<REST_schema.WhatsappSentLogResponse>;
  // Groups
  whatsappGetGroups: () => Promise<REST_schema.WhatsappListGroupsResponse>;
  whatsappCreateGroup: (
    request: REST_schema.WhatsappCreateGroupRequest
  ) => Promise<REST_schema.WhatsappCreateGroupResponse>;
  whatsappGetGroup: (groupId: string) => Promise<{ group: REST_schema.WhatsappGroupView }>;
  whatsappUpdateGroup: (groupId: string, request: { name: string }) => Promise<{ ok: boolean }>;
  whatsappDeleteGroup: (groupId: string) => Promise<{ ok: boolean }>;
  whatsappQueryGroupMembers: (
    groupId: string,
    request: REST_schema.WhatsappGroupMembersQueryRequest
  ) => Promise<REST_schema.WhatsappGroupMembersQueryResponse>;
  whatsappAddGroupMembers: (
    groupId: string,
    members: REST_schema.WhatsappGroupMemberInput[]
  ) => Promise<{ memberCount: number; invalidRows: { phone: string; reason: string }[] }>;
  whatsappRemoveGroupMembers: (
    groupId: string,
    phones: string[]
  ) => Promise<{ memberCount: number }>;
  // Audiences (קהלים) — auth-only recipient lists in the contacts area (same collection as the
  // legacy whatsappGroup fns; org-sharing aware). See /api/v1/audiences.
  audiencesList: () => Promise<REST_schema.WhatsappListGroupsResponse>;
  audiencesCreate: (
    request: REST_schema.WhatsappCreateGroupRequest
  ) => Promise<REST_schema.WhatsappCreateGroupResponse>;
  audiencesCreateFromSelection: (
    request: REST_schema.CreateAudienceFromSelectionRequest
  ) => Promise<REST_schema.CreateAudienceFromSelectionResponse>;
  audiencesGet: (
    audienceId: string
  ) => Promise<REST_schema.WhatsappGroupView & REST_schema.WhatsappGroupMembersQueryResponse>;
  audiencesUpdate: (audienceId: string, request: { name: string }) => Promise<{ ok: boolean }>;
  audiencesDelete: (audienceId: string) => Promise<{ ok: boolean }>;
  audiencesMembersQuery: (
    audienceId: string,
    request: REST_schema.WhatsappGroupMembersQueryRequest
  ) => Promise<REST_schema.WhatsappGroupMembersQueryResponse>;
  audiencesMembersAdd: (
    audienceId: string,
    members: REST_schema.WhatsappGroupMemberInput[]
  ) => Promise<REST_schema.WhatsappCreateGroupResponse>;
  audiencesMembersRemove: (audienceId: string, phones: string[]) => Promise<{ memberCount: number }>;
  // Blocked numbers (blacklist). Not gated on officialWhatsapp — also governs the extension
  // (WhatsApp Web) send loops. `whatsappBlacklistCheck` is FAIL-CLOSED: treat any throw as
  // "cannot send" (see utils/whatsapp/blockedGate.ts).
  whatsappBlacklistQuery: (
    request?: REST_schema.WhatsappBlacklistQueryRequest
  ) => Promise<REST_schema.WhatsappBlacklistQueryResponse>;
  whatsappBlacklistAdd: (request: REST_schema.WhatsappBlacklistAddRequest) => Promise<{ ok: boolean }>;
  whatsappBlacklistRemove: (
    request: REST_schema.WhatsappBlacklistRemoveRequest
  ) => Promise<{ ok: boolean }>;
  whatsappBlacklistCheck: (
    request: REST_schema.WhatsappBlacklistCheckRequest
  ) => Promise<REST_schema.WhatsappBlacklistCheckResponse>;
  // Campaigns
  whatsappCreateCampaign: (
    request: REST_schema.WhatsappCreateCampaignRequest
  ) => Promise<REST_schema.WhatsappCreateCampaignResponse>;
  /** Which listing-share template the number would use, and whether Meta approved it yet. */
  whatsappListingShareStatus: (
    request: REST_schema.WhatsappListingShareStatusRequest
  ) => Promise<REST_schema.WhatsappListingShareStatusResponse>;
  /** Adds the listing to each contact's minisite, then sends it as a one-template campaign. */
  whatsappListingShareSend: (
    request: REST_schema.WhatsappListingShareSendRequest
  ) => Promise<REST_schema.WhatsappListingShareSendResponse>;
  whatsappQueryCampaigns: (
    request: REST_schema.WhatsappQueryCampaignsRequest
  ) => Promise<REST_schema.WhatsappQueryCampaignsResponse>;
  whatsappGetCampaign: (campaignId: string) => Promise<WhatsappCampaign & { id: string }>;
  whatsappCampaignPreview: (
    campaignId: string
  ) => Promise<REST_schema.WhatsappCampaignPreviewResponse>;
  whatsappCampaignSetParamFallbacks: (
    campaignId: string,
    paramFallbacks: Record<string, string>
  ) => Promise<{ ok: boolean }>;
  /**
   * Everyone a campaign reached, delivery + response on one row — members of the sending number.
   * `activity` sort puts the responders first and complete; a `search` is one unpaged page.
   */
  whatsappCampaignRecipients: (
    campaignId: string,
    request: REST_schema.WhatsappCampaignRecipientsQueryRequest
  ) => Promise<REST_schema.WhatsappCampaignRecipientsQueryResponse>;
  whatsappCampaignAction: (
    campaignId: string,
    action: 'start' | 'pause' | 'resume' | 'cancel'
  ) => Promise<REST_schema.WhatsappCampaignActionResponse>;
  whatsappCampaignRetryFailed: (
    campaignId: string,
    contactIds?: string[]
  ) => Promise<REST_schema.WhatsappRetryFailedResponse>;
  /** "שליחה בכל זאת" — one person the recency window skipped; answers the outcome and the row's fresh view. */
  whatsappCampaignSendAnyway: (campaignId: string, waId: string) => Promise<REST_schema.WhatsappSendAnywayResponse>;
  /** A free-form reply to one responder from the campaign page (inside the thread's 24h window); answers the row's fresh view. */
  whatsappCampaignReply: (
    campaignId: string,
    waId: string,
    request: REST_schema.WhatsappCampaignReplyRequest
  ) => Promise<REST_schema.WhatsappCampaignReplyResponse>;
  /** The send pace / window, editable after create (before start or mid-run). Narrow-only. */
  whatsappCampaignSetPacing: (
    campaignId: string,
    request: REST_schema.WhatsappCampaignPacingRequest
  ) => Promise<REST_schema.WhatsappCampaignPacingResponse>;
  // Scheduled single sends (one_off campaigns riding the campaign engine)
  whatsappScheduledSendCreate: (
    request: REST_schema.WhatsappCreateScheduledSendRequest
  ) => Promise<REST_schema.WhatsappCreateScheduledSendResponse>;
  whatsappScheduledSendsQuery: (
    request?: REST_schema.WhatsappScheduledSendsQueryRequest
  ) => Promise<REST_schema.WhatsappScheduledSendsQueryResponse>;
  whatsappScheduledSendCancel: (campaignId: string) => Promise<{ ok: boolean }>;
  whatsappScheduledSendReschedule: (campaignId: string, scheduledFor: string) => Promise<{ ok: boolean }>;
};

/** What the server hands back so the browser can launch Meta's Embedded Signup popup. */
/** GET /api/v1/cards/page/:cardId — the server's listings/listingCardPages.ts, field by field. */
export type ListingCardPageListing = {
  id: string;
  street?: string;
  neighborhood?: string;
  city?: string;
  price?: number;
  oldPrice?: number;
  roomsCount?: number;
  squareMeter?: number;
  floor?: number;
  coverImageUrl?: string;
  privateSeller: boolean;
  rent: boolean;
};

export type ListingCardPage = {
  kind: "alert" | "price_drops" | "summary";
  createdAt: string;
  total: number;
  listings: ListingCardPageListing[];
};

export type WhatsappConnectConfig = {
  appId: string;
  configId: string;
  mode: WhatsappOnboardingMode;
  graphVersion: string;
};

/**
 * What Meta reported when an Embedded Signup session ended WITHOUT a connection, sent to
 * `POST /whatsapp/connect/diagnostics`.
 *
 * `sessionId` is the identifier Meta Direct Support asks for on any onboarding ticket, and Meta's
 * postMessage is the ONLY place it ever appears — it cannot be recovered afterwards from the
 * Graph API or the App Dashboard. `currentStep` is the abandonment point, and aggregated across
 * sessions it is the onboarding funnel.
 */
export type WhatsappConnectDiagnostics = {
  event?: string;
  sessionId?: string;
  currentStep?: string;
  errorCode?: string | number;
  errorMessage?: string;
  timestamp?: string;
  /** The flow that was launched — the two modes fail in different places. */
  mode?: WhatsappOnboardingMode;
};

export type WhatsappStatusResponse = {
  connected: boolean;
  status: WhatsappTenantStatus | null;
};

/** A conversation as the API returns it — the stored doc plus the derived 24h-window fields. */
export type WhatsappConversationView = WhatsappConversation & {
  id: string;
  windowOpen: boolean;
  windowRemainingMs: number;
};

export type WhatsappMessageView = WhatsappMessage & { id: string };

export type MarkNoSpamContactsResponse = {
  allContactsCount: number;
  markedSpamContacts: string[];
  skippedContactsCount: number;
  markedSpamContactsCount: number;
  unmarkedSpamContactsCount: number;
};

/**
 * GET /api/v1/locations/street-neighborhood wire shape. Declared here (not in
 * sharedSchema.ts) because the shared file is mirrored byte-for-byte from the
 * server repo, which does not declare this endpoint's wire type yet.
 */
export type StreetNeighborhoodInference = {
  /** Canonical neighborhood displayName — the corpus-dominant spelling. */
  neighborhood: string;
  /** The canonical neighborhood id (`n:<city>:<key>`). */
  locationId: string;
  /** Share of the street's stamped listings carrying this neighborhood. */
  share: number;
  /** How many stamped corpus listings sit on the street. */
  sampleSize: number;
};

export type StreetNeighborhoodResponse = { inference: StreetNeighborhoodInference | null };

export type _orgAdminStatsResponse_callsDataSchema = {
  phoneNumber: string;
  sumAnswered: number;
  sumNotAnswered: number;
  totalCalls: number;
  newLeads: number;
};

export type _orgAdminStatsResponse_userDataSchema = {
  userMail: string;
  userFullName: string;
  userPhotoURL: string;
  callsData: _orgAdminStatsResponse_callsDataSchema[];
  virtualNumbersCount: number;
  sumAnswered: number;
  sumNotAnswered: number;
  totalCalls: number;
  newLeadsFromVirtualNumbers: number;
  totalContactsCount: number;
  newLeadsFromWhatsappBots: number;
  calendarMeetingsCount: number;
  bulkMessagesSentCount: number;
};

export type orgAdminStatsResponseSchema = {
  userData: (_orgAdminStatsResponse_userDataSchema | null)[];
  teamMembersCount: number;
  totalWhatsappBotsCount: number;
  totalVirtualNumbersCount: number;
  totalNewLeadsFromVirtualNumbers: number;
  totalNewLeadsFromWhatsappBots: number;
  totalBulkMessagesSentCount: number;
  totalCalendarMeetingsCount: number;
  totalNotAnsweredCalls: number;
  totalCalls: number;
};

// ---------------------------------------------------------------------------
// CRM contacts list — POST /api/v1/contacts/list, backed by the Postgres mirror.
//
// Server-side list/filter/sort/search over contacts. Replaces downloading the
// whole contact book into the browser: measured, one real user holds 25,066.
// Scope is resolved server-side from `dashboard-orgs` — nothing here decides who
// may see what, and `ownerUserIds` can only NARROW the resolved scope.
// ---------------------------------------------------------------------------

export type CrmContactFilters = {
  tagsAny?: string[];
  tagsAll?: string[];
  tagsExclude?: string[];
  customerTypes?: string[];
  crmStatuses?: string[];
  crmSubStatusIds?: string[];
  isHot?: boolean;
  contactOrigins?: string[];
  budgetMin?: number;
  budgetMax?: number;
  budgetRentMin?: number;
  budgetRentMax?: number;
  roomsMin?: number;
  roomsMax?: number;
  floorsMin?: number;
  floorsMax?: number;
  propertyTypes?: string[];
  cities?: string[];
  neighborhoods?: string[];
  nameOrPhoneContains?: string;
  isInSpamDatabase?: boolean;
  optedOutOfBulkMessages?: boolean;
  /**
   * ISO-8601 instants. The server types these as `Date` (its first caller was
   * in-process) and revives the string form on the way in — JSON has no date type, so
   * a string is the only thing the browser can send. Sending anything else, or an
   * unparseable string, is dropped server-side rather than becoming an Invalid Date.
   *
   * `*Before` is EXCLUSIVE (`< bound`), matching `filterContacts`. `DateRangeFilter`'s
   * `buildActiveDateRange` already returns `to` as the start of the following day, so
   * the pair composes to "the whole of the chosen end day, included".
   */
  createdAfter?: string;
  createdBefore?: string;
  updatedAfter?: string;
  updatedBefore?: string;
  /**
   * Bulk-messaged at or after this ISO instant, on EITHER rail — an extension send or an
   * official-WhatsApp campaign. A contact that was never messaged matches no bound.
   */
  bulkMessagedAfter?: string;
  /** Bulk-messaged strictly BEFORE this ISO instant — exclusive, like every `*Before` above. */
  bulkMessagedBefore?: string;
  /**
   * Bulk-messaged at all, on either rail. `false` is "never" — the only way to reach those
   * contacts, since no bound matches a contact that has no time.
   */
  bulkMessaged?: boolean;
  /**
   * NOT bulk-messaged at or after this ISO instant — "didn't receive a message since". Unlike
   * the bounds above it KEEPS the never-messaged; the exact complement of `bulkMessagedAfter`.
   */
  notBulkMessagedSince?: string;
  /**
   * "Which contacts are missing details" — the axis every other field here assumes a
   * VALUE for. Mirrors `ContactFilters` in `server/api/services/contacts.service.ts`;
   * the rule itself lives in `server/api/services/crm/contactEmptiness.ts`.
   *
   *  - `missingFields` — ALL of these are empty (AND).
   *  - `presentFields` — ALL of these are filled (AND).
   *  - `missingAnyOf`  — at least ONE is empty (OR). This is the work-queue operator:
   *    without it "missing details" is N separate queries the client merges, which
   *    breaks both paging and the total.
   *
   * EMPTY MEANS JS-FALSY on both engines, so a literal `0` budget counts as missing
   * (measured: 173 contacts carry one).
   *
   * On the eight FACETED dimensions, `missingFields` ORs with that dimension's own
   * values rather than AND-ing — ticking "ללא" beside "בטיפול" means EITHER. On the
   * rest (budget / rooms / floors / name / notes / customerSeriousness) it is a plain
   * AND, so pairing one with that dimension's range filter is a contradiction.
   *
   * An unknown name is REJECTED by the server, not dropped: dropping it would widen the
   * result to the whole book, which reads as "the filter does nothing".
   */
  missingFields?: CrmContactEmptyField[];
  presentFields?: CrmContactEmptyField[];
  missingAnyOf?: CrmContactEmptyField[];
};

/**
 * The dimensions emptiness is defined for — `EMPTY_FILTER_FIELDS` in
 * `server/api/services/crm/contactEmptiness.ts`, mirrored name-for-name.
 *
 * The first eight are spelled identically to {@link crmFacetDimensions} on purpose: a
 * picker's "(none)" chip is exactly `missingFields: [<that dimension>]`, so the count on
 * the chip and the rows the chip returns come from one expression.
 *
 * `hot`, `isInSpamDatabase` and `optedOutOfBulkMessages` are absent because their mirror
 * columns COALESCE to `false` — "missing" is not a state they can be in. `phone` is
 * absent because the contact's document id IS the phone (47,726 of 47,726).
 *
 * `name` and `notes` are gated server-side on the caller's `seeName` / `seeNotes`
 * redaction flags and FAIL CLOSED — "has no note" is one bit of text a redacted viewer
 * may not read, and one they could harvest for a whole agency in a single request.
 */
export const crmContactEmptyFields = [
  'crmStatus',
  'crmSubStatus',
  'contactOrigin',
  'customerType',
  'tag',
  'city',
  'neighborhood',
  'propertyType',
  'name',
  'budget',
  'budgetRent',
  'rooms',
  'floors',
  'notes'
] as const;
export type CrmContactEmptyField = typeof crmContactEmptyFields[number];

/**
 * `lastBulkMessageSentTime` orders by the row field of the same name — when the contact was
 * last bulk-messaged. The only key with no index behind it: the server scans the book for it
 * (~0.2 s a page on the largest book in the fleet), so it is a sort a realtor asks for, never
 * a default.
 */
export type CrmContactsSortKey = 'updatedTime' | 'createdTime' | 'budget' | 'name' | 'lastBulkMessageSentTime';

/**
 * The dimensions the server both counts and returns real values for.
 *
 * Ranges (budget, rooms, floors, dates) are deliberately absent: a histogram is a
 * different control, and the filter panel renders those as plain min/max inputs.
 */
export const crmFacetDimensions = [
  'crmStatus',
  'crmSubStatus',
  'hot',
  'customerType',
  'contactOrigin',
  'tag',
  'city',
  'neighborhood',
  'propertyType'
] as const;
export type CrmFacetDimension = typeof crmFacetDimensions[number];

/**
 * `isNone` marks the "no value" bucket, counted with the SAME expression
 * `missingFields: [<dimension>]` filters by — so the chip's number and the rows it
 * returns cannot disagree.
 *
 * It is a flag rather than a magic value because a realtor can perfectly well have
 * imported a tag literally named "(none)", and the bucket is selectable, so the two
 * have to stay distinguishable.
 */
export type CrmContactsFacetValue = { value: string; count: number; isNone?: boolean };

/**
 * What the filter panel renders its pickers from.
 *
 * `values` carries each dimension's REAL values with counts, ordered by frequency and
 * capped at 60 per dimension — a realtor cannot filter by a tag they cannot name, and
 * the values are Hebrew, stored lowercased, and invented by whoever imported the CSV.
 *
 * A dimension is counted with its OWN filter omitted, so selecting one status does not
 * zero the other statuses. With two or more faceted dimensions active a count is exact
 * for the dimension being edited and an upper bound for the rest.
 *
 * Scalar dimensions (crmStatus, crmSubStatus, contactOrigin) report NULL as the literal
 * value `"(none)"`. There is no filter that can express "is null", so the panel does not
 * offer those rows — see `CRM_FACET_NONE` in the filter model.
 */
export type CrmContactsFacets = {
  byCrmStatus: Record<string, number>;
  hot: number;
  values: Partial<Record<CrmFacetDimension, CrmContactsFacetValue[]>>;
};

export type CrmContactsListRequest = {
  view?: 'mine' | 'org';
  orgId?: string;
  ownerUserIds?: string[];
  filters?: CrmContactFilters;
  /**
   * The filters the קונים/מוכרים tab applies with nothing else selected. The server counts
   * `scopeTotal` under them (the denominator is the TAB's size, not the whole book) and
   * treats `filters` equal to them as not narrowing. Count line only — `filters` must
   * still carry the pin.
   */
  scopeFilters?: CrmContactFilters;
  search?: string;
  sortBy?: CrmContactsSortKey;
  sortDir?: 'asc' | 'desc';
  limit?: number;
  cursor?: string;
  /** Counts for the always-visible chip row (status + 🔥). Cheap. */
  includeFacets?: boolean;
  /**
   * Every faceted dimension's real values and counts. Measured at ~370ms on a
   * 47k-row org versus ~77ms for the chips alone — ask for it ONCE when the panel
   * opens, never per keystroke.
   */
  includeFacetValues?: boolean;
  includeDeleted?: boolean;
  /**
   * ONE ROW PER PERSON — the agency tab.
   *
   * Without it `view: "org"` returns one row per (owner, contact), so a person held by
   * eight agents appears eight times. With it the server picks one representative copy —
   * the caller's own where they have one, otherwise the most complete — and every count
   * on the response switches to counting PEOPLE.
   *
   * Ignored under `view: "mine"`, where the contact id is already unique in the book.
   */
  collapse?: 'contact';
};

export type CrmContactRow = {
  ownerUserId: string;
  /**
   * The BOOK this row came from — `owner_user_id` on the mirror: the owner's email for a
   * personal book, `org:<orgId>` for an agency's pooled book (plan D4). Equal to
   * `ownerUserId` today. The pair is what the server's read door stamps on every row so no
   * client ever derives "mine" from a path or an owner again.
   */
  bookKey: string;
  /** The members holding this person in a pooled book; `[]` for a personal book (always, today). */
  holderIds: string[];
  /**
   * `/contacts/lookup` only: does the CALLER hold this row. Always true on a personal book;
   * on a pooled book the lookup answers over the whole office, and this says which rows are
   * already on the caller's own list. Absent on every other endpoint's rows.
   */
  held?: boolean;
  ownerName: string | null;
  ownerAvatarUrl: string | null;
  /** The contact's Firestore doc id — the international phone number. */
  contactId: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  additionalNotes: string | null;
  contactOrigin: string | null;
  crmStatus: string | null;
  crmSubStatus: string | null;
  crmIsHot: boolean;
  customerSeriousness: string | null;
  customerTypes: string[];
  tags: string[];
  cities: string[];
  neighborhoods: string[];
  /**
   * The same interest GROUPED by city — what the "אזורים מבוקשים" column renders.
   *
   * `cities` and `neighborhoods` above are two independently-DISTINCTed arrays, so the
   * pairing between them is not recoverable here: measured on a real contact,
   * `neighborhoods` carries רמת אשכול once while the document pairs it with three
   * different cities. Both are kept — the flat arrays are what the FILTER path uses,
   * because they are the indexed columns.
   *
   * An entry with no `neighborhoods` means the whole city, which is the majority case.
   */
  interestedAreas: { city: string; neighborhoods: string[] }[];
  /**
   * The interest array exactly as the DOCUMENT holds it — city, neighborhood AND street per
   * entry. `interestedAreas` above is the screen's grouping and cannot be turned back into
   * this; the export and the lookup layer's contact projection read this one.
   */
  interestedInLocations: { city?: string; neighborhood?: string; street?: string }[];
  propertyTypes: string[];
  /**
   * Linked SELLER-side listing ids (`real-estate-listings` doc ids), written by the edit
   * dialog's פרופיל מוכר/משכיר pickers. Plain jsonb extractions in the projection —
   * display-only, never filterable (no generated column behind them). What seeds
   * `POST /contacts/listing-links`. NOT `favorite-listings-of-contact`, which is the
   * buyer-side matches store.
   */
  sellingListingIds: string[];
  rentingOutListingIds: string[];
  budget: number | null;
  budgetMin: number | null;
  budgetRent: number | null;
  roomsMin: number | null;
  roomsMax: number | null;
  /**
   * Blocked-for-broadcast state, for the row/menu UI. A display MIRROR of the contact flags —
   * the blocked-numbers list (`whatsapp-suppressions`) is the enforcement record, and every
   * send path re-checks it server-side (or fail-closed via /blacklist/check) at send time.
   */
  optedOutOfBulkMessages: boolean;
  isInSpamDatabase: boolean;
  /**
   * When this person was last bulk-messaged, on either rail — the later of the extension's
   * stamp and the official campaign's, which live in two document fields. The time only.
   * Null when they never were.
   */
  lastBulkMessageSentTime: string | null;
  createdTime: string | null;
  updatedTime: string | null;
};

// ---------------------------------------------------------------------------
// POST /api/v1/contacts/lookup, /presence, /summary
//
// MIRRORED from `server/api/services/crm/contactsPg.service.ts` and
// `contactsLookupRules.ts`.
//
// What every screen OTHER than the contacts page asks about the book. Each used to answer
// its question by downloading the caller's entire book into the browser and searching the
// array; these are the same questions asked of the Postgres mirror. All three are about
// the caller's OWN book — colleagues' copies of a person are `/contacts/holders`.
// ---------------------------------------------------------------------------

/**
 * How many phones one lookup may carry. Above it the server REFUSES (400) rather than
 * trimming — a missing row reads as "not in my book". `contactLookupBatcher` chunks to this.
 */
export const CONTACTS_LOOKUP_MAX_IDS = 200;

/** How many phones one presence check may carry — an import file's worth per request. */
export const CONTACTS_PRESENCE_MAX_IDS = 5000;

export type ContactsLookupResponse = {
  /** The caller's rows for the ids asked about. An id with no row here is NOT in the book. */
  contacts: CrmContactRow[];
};

/**
 * How many listings one `/contacts/listing-sellers` request may carry — a page of the listings
 * board. Above it the server REFUSES (400) rather than trimming, for the lookup's reason.
 */
export const CONTACTS_LISTING_SELLERS_MAX_IDS = 200;

export type ContactsListingSellersResponse = {
  /**
   * The caller's rows that link at least one listing asked about as its seller / landlord
   * (`sellingListingIds` / `rentingOutListingIds` say which). A listing no row links has
   * nobody of the caller's as its seller side.
   */
  contacts: CrmContactRow[];
};

/** One phone the caller already has. Phones they do not have are simply absent. */
export type ContactPresence = {
  contactId: string;
  /** False = the ללא שם queue's rule: no full name, no first name, no last name. */
  hasName: boolean;
  optedOutOfBulkMessages: boolean;
  /**
   * Does the CALLER hold this phone. Always true on a personal book. On a pooled book the
   * check answers over the whole OFFICE — a phone the office knows is not a new contact, and
   * the import puts the caller on its list instead of minting a second record — and this
   * says whether it is already on the caller's own list. Optional only for the deploy
   * window in which an older server answers without it; read absent as held.
   */
  held?: boolean;
};

export type ContactsPresenceResponse = { contacts: ContactPresence[] };

/**
 * The home page's contact tiles. Every number is the count the contacts list prints for
 * the same filter, so a tile can never disagree with the list behind it.
 */
export type ContactsSummaryResponse = {
  total: number;
  createdLast7Days: number;
  /** Bulk-messaged in the last 30 days, on either rail — `CrmContactFilters.bulkMessagedAfter`. */
  messagedLast30Days: number;
  /** The ללא שם work queue: how many, and the ten most recently created. */
  unnamed: { count: number; newest: CrmContactRow[] };
};

export type CrmContactsListResponse = {
  contacts: CrmContactRow[];
  /** Present ONLY when the page came back full. Absence means "no more". */
  nextCursor: string | null;
  /**
   * EXACT. The cap is 200,000 — far past any real agency — so `totalHitsCapped` is
   * false in practice and this is the true number, not a page-window count.
   */
  totalHits: number;
  /** True when totalHits hit the server cap — render "200,000+", never "200,000". */
  totalHitsCapped: boolean;
  /**
   * Contacts in scope ignoring filters AND search — the denominator in "12 מתוך 47,872".
   * Null when nothing is narrowing, because `totalHits` is then the same number.
   */
  scopeTotal: number | null;
  /**
   * `scopeTotal` hit the same cap and is a FLOOR, not the number.
   *
   * `totalHits` has always carried `totalHitsCapped`; its denominator carried nothing,
   * so a truncated book size rendered as exact — "12 מתוך 200,000" with no hint that the
   * second number is a wall. Same fact, same treatment.
   */
  scopeTotalCapped?: boolean;
  facets?: CrmContactsFacets;
  scopeApplied: {
    view: string;
    orgId: string | null;
    visibleOwnerCount: number;
    redactedNotes: boolean;
  };
  /** The server refused the requested sort (e.g. by a redacted field) and used the default. */
  sortDegraded?: boolean;
};

// ---------------------------------------------------------------------------
// POST /api/v1/contacts/holders — who else in my agency has this person
// ---------------------------------------------------------------------------

/**
 * One agent's copy of a person, at whatever detail level the agency's `pool` permits.
 *
 * The optional half below the divider is present exactly when the RESPONSE says
 * `pool: "full"` — the server does not select those columns at all otherwise, so a
 * `basic` holder is not a full one with nulls in it. Render off the response's `pool`,
 * never off whether a field happens to be present.
 */
export type CrmContactHolder = {
  ownerUserId: string;
  ownerName: string | null;
  ownerAvatarUrl: string | null;
  contactId: string;
  /** BASIC and above — the three facts an agent always gets about a colleague's copy. */
  name: string | null;
  /**
   * `COALESCE(doc.createdTime, snapshot.createTime)`.
   *
   * The fallback is not defensive: `handleOnCreateDocument` writes `doc.createdTime` in a
   * SECOND asynchronous write, so a brand-new contact carries neither timestamp for a
   * moment. Measured 2026-08-23: 40 of 47,726 live mirror rows need it, and it rescues
   * all 40.
   */
  createdTime: string | null;
  /**
   * When this copy last CHANGED — `GREATEST(doc.updatedTime, snapshot.updateTime)`.
   *
   * ⚠️ Two columns, because neither is sufficient alone, and this is measured rather than
   * argued. `doc.updatedTime` is skipped by its Cloud Function whenever
   * `skipUpdatedTimeAroundDate` is set, so it MISSES real edits — against the CRM audit
   * trail it tracked only 4 of 7 genuine realtor writes. `snapshot.updateTime` catches
   * every write including machine ones, which is why 86.6% of the corpus (41,337 of
   * 47,726) carries it in a single month: a mass Cloud-Function pass. The median
   * disagreement on multi-holder contacts — the population the agency panel renders — is
   * 41.7 days, p90 277.
   *
   * The later of the two is the only statement that is TRUE as rendered ("nothing has
   * changed since this moment"), which is what makes an EXACT timestamp safe to show.
   *
   * ⚠️ LAST CHANGE, never last contact. It moves on writes no human made, so a label like
   * `פנייה אחרונה` would be a lie — the same one `CrmContactTableRow` already refuses.
   */
  lastChangeTime: string | null;
  /**
   * @deprecated Raw `doc.updatedTime`. Kept for one release so the deploy order of the
   * server and this app cannot matter. Prefer `lastChangeTime`.
   */
  updatedTime: string | null;
  // ── `pool: "full"` only ───────────────────────────────────────────────────
  crmStatus?: string | null;
  crmSubStatus?: string | null;
  crmIsHot?: boolean;
  customerSeriousness?: string | null;
  customerTypes?: string[];
  tags?: string[];
  cities?: string[];
  propertyTypes?: string[];
  budget?: number | null;
  budgetRent?: number | null;
  contactOrigin?: string | null;
  additionalNotes?: string | null;
};

export type CrmContactHoldersRequest = {
  orgId?: string;
  /** Contact ids from a page already served to this caller. Capped server-side at 200. */
  contactIds: string[];
};

/**
 * `POST /api/v1/contacts/holders` — NEVER refuses.
 *
 * `enabled: false` is the normal answer for most of the fleet: a solo realtor, an agent
 * whose agency has not granted the pool, or an agency that switched it off. It is not an
 * error and not a permission message to render on a contact row — the caller simply gets
 * no avatars and no expander.
 */
export type CrmContactHoldersResponse = {
  enabled: boolean;
  pool: 'none' | 'basic' | 'full';
  /** contactId → every holder in scope, the caller's own copy INCLUDED. Capped at 20 each. */
  holders: Record<string, CrmContactHolder[]>;
  /** contactId → the TRUE number of holders, even where the list above was capped. */
  counts: Record<string, number>;
};

// ---------------------------------------------------------------------------
// POST /api/v1/contacts/update — the only contact WRITE the server exposes
// ---------------------------------------------------------------------------

/**
 * A patch against one contact.
 *
 * Every key is checked against a server-side whitelist and an unknown one is REJECTED,
 * not dropped — a silently dropped field is a save that reports success and changes
 * nothing, which is the worst possible outcome for a quick-fill whose entire job is that
 * the value sticks.
 *
 * Deliberately NOT writable, each for its own reason:
 *  - `callerPhoneNumber` — the document id IS the phone.
 *  - `contactFullName`, `interestedInCitiesDistinct`, `interestedInNeighborhoodsDistinct`
 *    — stamped by Cloud Functions from the fields below; writing them races the function
 *    that owns them. Write `callerFirstName`/`callerLastName`/`interestedInLocations`
 *    and the derived fields follow.
 *  - `customerType` / `customerSeriousness` — legacy mirrors the server derives from the
 *    canonical fields. Sending them directly is how the two halves drift apart, which is
 *    also why this endpoint makes the browser's own `build*Update` normalisation
 *    unnecessary.
 *  - `organizationIds` — an access-control input, never client-writable.
 */
/**
 * What the browser OBSERVED when it sent one bulk WhatsApp message.
 *
 * An outcome, never a field name. `lastBulkMessageReceived`,
 * `optedOutOfBulkMessages` and `bulkMessagingNumberDoesntExists` are pipeline results
 * that the generic contact-write whitelist deliberately refuses ("a client value here is
 * a guess overwriting a fact"); reporting the outcome instead keeps that rule intact,
 * because the browser IS the pipeline for an extension send and the server owns the
 * mapping onto document fields.
 */
export type CrmBulkMessageOutcome = 'sent' | 'number_missing';

export type CrmRecordBulkMessageRequest = {
  view?: 'mine' | 'org';
  orgId?: string;
  ownerUserId?: string;
  contactId: string;
  outcome: CrmBulkMessageOutcome;
  /** Required when `outcome` is "sent". `sentTime`, `sentTo` and `updatedFrom` are set
   *  by the server and anything sent for them is ignored. */
  sentMessage?: { message?: string; mediaLink?: string };
};

export type CrmRecordBulkMessageResponse = {
  ownerUserId: string;
  contactId: string;
  outcome: CrmBulkMessageOutcome;
  /** False when nothing was written: the outcome was already recorded, or `notAContact`. */
  written: boolean;
  /**
   * The recipient is not a contact in that book, so there was nothing to stamp. Present only
   * when true. A normal outcome for a send loop (a file's numbers, the owners on rental ads,
   * a colleague's buyers), which is why it is an answer and not a 404.
   */
  notAContact?: true;
};

export type CrmContactUpdateRequest = {
  view?: 'mine' | 'org';
  orgId?: string;
  /**
   * Whose book. Defaults to the caller; any other owner is INTERSECTED with the
   * caller's resolved scope, so naming one can never reach outside it.
   */
  ownerUserId?: string;
  /** The Firestore document id — the contact's international (E.164) phone number. */
  contactId: string;
  fields: Record<string, unknown>;
};

/**
 * The endpoint does NOT create: a missing document is a 404 rather than a silent
 * `set(merge:true)`, which would mint a contact under an owner the caller may only READ.
 */
export type CrmContactUpdateResponse = {
  ownerUserId: string;
  contactId: string;
  /** The keys actually written, after whitelisting and after dropping no-op values. */
  changedKeys: string[];
  /**
   * The PERSISTED row, read back from the mirror after the write is drained — not an
   * echo of the patch. The mirror has been fleet-wide since 2026-08-25, so this is null
   * only when the inline drain failed and the scheduled one has yet to repair it.
   */
  contact: CrmContactRow | null;
  /**
   * The whole document as persisted, read back after the write and redacted with the
   * request's own flags (plan D2): `useContactWrites` writes it into the document store so
   * an open panel repaints in the response tick, before any signal lands. Null only when
   * the read-back failed (logged and metered server-side). Dates cross the wire as ISO
   * strings; `reviveContactDocument` restores them.
   */
  doc: RealEstateContact | null;
  /** `crm_contact.version` after the write; 0 while the mirror has no row (or no column yet). */
  version: number;
  mirrorSynced: boolean;
  scopeApplied: { view: string; orgId: string | null; visibleOwnerCount: number };
  /**
   * Present when the write touched `optedOutOfBulkMessages`. `kept: true` means an UNTICK met a
   * blocked-list row the flag did not create (a human's הסר, a Meta verdict, a block placed on
   * the blocked page, a dead number) — the row stands, the flag is true again, and `message`
   * says so in Hebrew. The blocked page is the only place such a row is lifted.
   */
  blockedList?: {
    kept: boolean;
    reason?: WhatsappSuppressionReason;
    sourceHebrew?: string;
    message?: string;
  };
};

// ---------------------------------------------------------------------------
// POST /api/v1/contacts/create — and /bulk-create, the file import's upsert
//
// MIRRORED from `server/api/services/crm/contactsCreate.service.ts`.
//
// Creating used to be the one customer-facing contact write with no server involvement:
// the dialog and the import both wrote Firestore straight from the browser, so the field
// whitelist, the owner check, the audit row and the blocked-list reconciliation applied to
// every EDIT and to no CREATE — and, because only the Cloud Function's queue entry and the
// `* * * * *` drain could reach Postgres, a new contact was missing from the CRM page for
// up to a minute. Both endpoints drain their own keys inline, so the contact is in the
// mirror by the time the response lands.
// ---------------------------------------------------------------------------

export type CrmContactCreateRequest = {
  view?: 'mine' | 'org';
  orgId?: string;
  /** Whose book. Defaults to the caller; any other owner is intersected with the resolved scope. */
  ownerUserId?: string;
  /**
   * The new contact's phone in any parseable form. The server normalizes it to E.164 and
   * files the document under THAT — so the id in the response is the one to use, not this.
   */
  contactId: string;
  /** Same whitelist as `/contacts/update`. An unknown key is REJECTED, never dropped. */
  fields: Record<string, unknown>;
  /** Stamped as `contactOrigin` when `fields` carries none. */
  defaultContactOrigin?: string;
};

/**
 * ⚠️ 409 (`CRM_CONFLICT`) when the number is already in that book — including when the
 * contact standing there is SOFT-DELETED. The dialog renders `message` and offers to open
 * the existing contact; silently merging into it would join two people's histories.
 */
export type CrmContactCreateResponse = {
  ownerUserId: string;
  /** The NORMALIZED id the contact was filed under — may differ from what was sent. */
  contactId: string;
  changedKeys: string[];
  /**
   * The PERSISTED row. Null when the inline drain failed and the scheduled one has yet to
   * repair it — so a caller that inserts this into a list must tolerate null rather than
   * treat it as "the create failed".
   */
  contact: CrmContactRow | null;
  /** The whole document as persisted — see `CrmContactUpdateResponse.doc`. */
  doc: RealEstateContact | null;
  /** `crm_contact.version` after the write; 0 while the mirror has no row (or no column yet). */
  version: number;
  mirrorSynced: boolean;
  scopeApplied: { view: string; orgId: string | null; visibleOwnerCount: number };
  blockedList?: { kept: boolean; message?: string };
  /**
   * POOLED agency only (plan D4): the office already had a live record of this phone, so
   * nothing was created — the caller was put on its holder list ("my contacts") and the
   * office's record is what `contact` / `doc` carry. `changedKeys` is empty. The dialog
   * says so in one sentence and opens the existing card; it is a success, not a 409.
   */
  alreadyInOffice?: true;
};

/** The file import sends at most this many rows per request; the server rejects more. */
export const MAX_BULK_CREATE_CONTACTS = 200;

export type CrmContactsBulkCreateRequest = {
  view?: 'mine' | 'org';
  orgId?: string;
  ownerUserId?: string;
  contacts: { contactId: string; fields: Record<string, unknown> }[];
  defaultContactOrigin?: string;
};

/**
 * An UPSERT, unlike `/create` — overwriting known contacts is what an import is for, and
 * the uploader has already shown the realtor which rows that affects.
 *
 * A malformed row fails ALONE and is reported by id; the rest of the chunk still lands.
 * Callers must render `failed` rather than assume a 200 means every row was written.
 */
export type CrmContactsBulkCreateResponse = {
  ownerUserId: string;
  created: number;
  updated: number;
  failed: number;
  mirrorSynced: boolean;
  results: {
    /** What was SENT — line results up on this, since normalization can change the id. */
    requestedContactId: string;
    contactId: string | null;
    status: 'created' | 'updated' | 'failed';
    error?: string;
  }[];
};

// ---------------------------------------------------------------------------
// Bulk paths — delete / bulk-update / export
//
// MIRRORED from `server/api/services/crm/contactsBulkRules.ts` and
// `contactsBulk.service.ts`. The union below is the server's `ContactSelection`; the
// request/response shapes are its three endpoints. This is a mirror, not a second
// design — the server re-derives everything and refuses anything it disagrees with.
// ---------------------------------------------------------------------------

/**
 * One contact, before scoping. A bare string means "that contact in MY book" — the
 * server defaults the owner to the caller and then intersects whatever it was given with
 * the caller's resolved scope, so naming someone else's owner can never reach outside it.
 */
export type CrmContactRef = { ownerUserId?: string; contactId: string };

/**
 * WHAT A BULK ACTION ACTS ON — one union for `/delete`, `/bulk-update` and `/export`.
 *
 * The CRM table is PAGED: it holds 50 rows and knows there are 8,102 matches. Every bulk
 * action a realtor actually wants is expressed against the MATCH SET, not against the
 * handful of ids the browser happens to be holding — so a selection is either the ids
 * they ticked or the QUERY they were looking at, and the server re-resolves the query.
 *
 * `filters` / `search` / `ownerUserIds` / `includeDeleted` are the SAME four fields the
 * list request carries. THEY MUST BE FORWARDED BYTE-IDENTICALLY from what the list
 * request used: any client-side rewriting makes the acted-on set differ from the
 * displayed one with nothing on screen to show it.
 */
export type CrmContactSelection =
  | { mode: 'ids'; contacts: (CrmContactRef | string)[] }
  | {
      mode: 'filter';
      filters?: CrmContactFilters;
      search?: string;
      ownerUserIds?: string[];
      includeDeleted?: boolean;
      /**
       * Contacts to subtract — "select all, then untick three". Capped at
       * {@link CRM_MAX_SELECTION_EXCLUSIONS} server-side: it becomes one `NOT EXISTS`
       * over two bound arrays, and an unbounded one is an unbounded request body, i.e.
       * exactly what filter mode exists to prevent, re-entering through the back door.
       */
      exclude?: (CrmContactRef | string)[];
      /**
       * How many contacts the client BELIEVES it is acting on. REQUIRED, and enforced:
       * the server resolves its own count inside a `REPEATABLE READ` snapshot and refuses
       * the request — naming both numbers — if they disagree.
       *
       * It must be the number the USER WAS SHOWN (`totalHits` from the same `/list`
       * response the table is rendering), never a recomputed one. It is the only signal
       * the server has that the acted-on set is the set the human approved.
       */
      expectedCount: number;
    };

/** `MAX_SELECTION_EXCLUSIONS` server-side. Past this, the honest answer is to narrow the filter. */
export const CRM_MAX_SELECTION_EXCLUSIONS = 1000;

/** Every bulk request carries the same scope pair the list does. */
type CrmBulkScope = { view?: 'mine' | 'org'; orgId?: string };

/** Where the acted-on rows came from — `'filter'` means the SERVER resolved the set. */
export type CrmSelectionMode = 'ids' | 'filter';

export type CrmBulkScopeApplied = { view: string; orgId: string | null; visibleOwnerCount: number };

/**
 * `POST /api/v1/contacts/delete`.
 *
 * The legacy shape (`{ contacts: [...] }`, or a bare array) is still accepted by the
 * server; this client always sends `selection`, because the two modes are one decision
 * and splitting them across two body shapes is how they drift.
 */
export type CrmContactsDeleteRequest = CrmBulkScope & {
  selection: CrmContactSelection;
  /**
   * POOLED agency only (plan D4): a delete takes the caller OFF the records' holder lists by
   * default and the office's records stand; `true` deletes the office's records themselves,
   * which needs the agency's `delete` permission AND `pool: 'full'`. Ignored on a personal
   * book, where a delete is a delete. Only a literal `true` means it — the server refuses
   * any other value.
   */
  deleteOfficeRecord?: true;
};

/** What a pooled delete DID — see `CrmContactsDeleteResponse.mode`. */
export type CrmContactsDeleteMode = 'holder' | 'record';

export type CrmContactsDeleteSkip = {
  ownerUserId: string;
  contactId: string;
  /**
   * `not_found` — no such document. `already_deleted` — it already carries a `deletedAt`.
   * `not_held` — a pooled "remove from my list" on a person who was not on the caller's list.
   */
  reason: 'not_found' | 'already_deleted' | 'error' | 'not_held';
};

export type CrmContactsDeleteResponse = {
  requested: number;
  deleted: number;
  /** ISO. The stamp written onto every deleted document — what a recycle bin would sort on. */
  deletedAt: string;
  skipped: CrmContactsDeleteSkip[];
  mirrorSynced: boolean;
  selectionMode: CrmSelectionMode;
  scopeApplied: CrmBulkScopeApplied;
  /**
   * `record` — the documents are gone (every personal-book delete, and a pooled one with
   * `deleteOfficeRecord`); `holder` — the caller was taken off the records' holder lists and
   * the office's records stand. `deleted` counts whichever. Absent from an older server's
   * answer, which can only have deleted records.
   */
  mode?: CrmContactsDeleteMode;
};

/**
 * `POST /api/v1/contacts/bulk-update`.
 *
 * ⚠️ SET, NOT ADD. The patch REPLACES what it writes: `tags: ['VIP']` on 2,000 contacts
 * leaves those 2,000 with exactly one tag. The rule that makes that survivable is that
 * EMPTY VALUES ARE DROPPED server-side — an empty array, an empty string, whitespace —
 * so an untouched form field cannot wipe the field on every selected contact. The
 * consequences are real and accepted: there is no "add tag X while keeping theirs", and
 * no way to CLEAR a text or array field in bulk. An explicit `null` still clears the
 * scalars whose validators accept it (budgets, rooms, floors, crmStatus), because a
 * `null` is unambiguously typed by a human while `''` is what an untouched input looks
 * like. A patch that is entirely empty values is a 400, not a "0 changed" success.
 */
export type CrmContactsBulkUpdateRequest = CrmBulkScope & {
  selection: CrmContactSelection;
  /** Same whitelist as `/contacts/update`. An unknown key is REJECTED, never dropped. */
  fields: Record<string, unknown>;
};

export type CrmContactsBulkUpdateSkip = {
  ownerUserId: string;
  contactId: string;
  /**
   * `unchanged` is the interesting one: every value already matched, so no write was
   * spent and no Cloud Function fired. It is a success, not a failure — surface it as
   * "already had these values", never as an error.
   */
  reason: 'not_found' | 'deleted' | 'unchanged' | 'error';
};

export type CrmContactsBulkUpdateResponse = {
  requested: number;
  updated: number;
  /** The keys the patch could write, AFTER the empty values were stripped. */
  writtenKeys: string[];
  skipped: CrmContactsBulkUpdateSkip[];
  mirrorSynced: boolean;
  selectionMode: CrmSelectionMode;
  scopeApplied: CrmBulkScopeApplied;
  /**
   * Contacts whose UNTICK of `optedOutOfBulkMessages` was overruled by a blocked-list row the
   * flag did not create (their flag is true again). Present only when the patch touched the flag.
   */
  blockedListKept?: number;
};

/**
 * `POST /api/v1/contacts/export`.
 *
 * Takes the LIST body (so "export what I am looking at" is literally forwarding the
 * request the screen already built) and, optionally, a `selection` that WINS over it.
 * A filter selection is NOT redundant with the body-level filters: only a selection can
 * carry `exclude`, and silently ignoring exclusions would ship rows the realtor unticked.
 */
export type CrmContactsExportRequest = CrmBulkScope & {
  selection?: CrmContactSelection;
  filters?: CrmContactFilters;
  search?: string;
  ownerUserIds?: string[];
  sortBy?: CrmContactsSortKey;
  sortDir?: 'asc' | 'desc';
  includeDeleted?: boolean;
};

/**
 * The parsed CSV response.
 *
 * `filename` and `rowCount` ride HEADERS (`Content-Disposition`, `X-Contacts-Exported`),
 * both of which the server explicitly lists in `Access-Control-Expose-Headers` — without
 * that the browser hands over the body and hides the headers, and the download gets a
 * generated name nobody asked for.
 */
/** The exported workbook, as the browser received it. `.xlsx`, like the contacts page's own export. */
export type CrmContactsExportFile = { blob: Blob; filename: string; rowCount: number };

export type CrmLeadsBoardRow = {
  contactId: string;
  displayName: string | null;
  heldByAgents: number;
  agents: string[];
  distinctStatuses: number;
  firstCapturedTime: string | null;
  lastTouchedTime: string | null;
};

export type CrmLeadsBoardResponse = {
  rows: CrmLeadsBoardRow[];
  scopeApplied: unknown;
};

// ---------------------------------------------------------------------------
// CRM organization permissions — the agency surface at /dashboard/crm/settings.
//
// MIRRORED from the server: the four permission types hang off `Organization` and
// the request/response shapes live in the `REST_schema` CRM ORG section, both in
// `server/types/sharedSchema.ts`. They are declared HERE rather than in
// `src/@types/sharedSchema.ts` (the byte-for-byte mirror of that file) only because
// the CRM wire types have always lived in this file; the sharedSchema mirror is a
// separate change. Keep them identical to the server's — this is a mirror, not a
// second design.
//
// THE MODEL, IN ONE LINE: `crmPermissions[lowercased email] ?? DEFAULTS[role]`, where
// role is `admin` iff the email is in `dashboard-orgs.admins`. Both fields are
// OPTIONAL fields on the organization document itself, beside the roster they are
// about — so an agency that has configured nobody stores nothing at all, and the
// answer is never out of step with the membership it is derived from. The previous
// model (a Postgres control plane, an 8-entry capability vocabulary and
// grant/revoke deviation arrays) is gone; its tables were EMPTY in production
// precisely because the roster it had to join against lives in Firestore.
//
// ⚠️ AN ADMIN'S PERMISSIONS ARE ADVISORY, A MEMBER'S ARE ENFORCED. `firestore.rules`
// grants an org admin a direct browser read of a teammate's whole
// `dashboard-users-v2/{other}/**` subtree (`isUserOrManagerOfUser`, 9 rule sites) —
// accepted, and deliberately not being changed. So for an admin these fields shape
// what the product OFFERS, not what they can reach. For a non-admin this server is
// the only cross-user path, so their permissions genuinely hold. No copy on this
// screen may imply otherwise.
// ---------------------------------------------------------------------------

/**
 * How much of ANOTHER member's contact you see when you encounter one.
 *
 *   none  — nothing; the record is not shown.
 *   basic — the identifying fields, free text redacted. This is today's blur
 *           (`blurOrgDataForNonAdminUser`, placeholder '****').
 *   full  — everything, notes and call summaries included.
 *
 * ORDERED, least → most, and the UI must render it as one ordered choice: it is a
 * level, not two independent flags.
 */
export const crmPoolAccessLevels = ['none', 'basic', 'full'] as const;
export type CrmPoolAccess = typeof crmPoolAccessLevels[number];

/** Role within one agency: `admin` iff the email is in `dashboard-orgs.admins`, else `member`. */
export const crmOrgRoles = ['admin', 'member'] as const;
export type CrmOrgRole = typeof crmOrgRoles[number];

/**
 * What one agency member may do. Four fields — the whole vocabulary.
 *
 * `pool` and `browse` are SEPARATE AXES and must not be collapsed into one level.
 * Today's non-admin already has the first without the second: they may open a
 * teammate's contact when they bump into one (blurred), but may not list the agency's
 * book. One combined level would silently either grant browse to every agent or take
 * the blurred detail away from all of them.
 */
export type CrmMemberPermissions = {
  pool: CrmPoolAccess;
  /** May browse/search the WHOLE agency book. Branded "מאגר משרדי" in the UI. */
  browse: boolean;
  /** May download/export contacts. */
  export: boolean;
  /** May delete contacts. */
  delete: boolean;
};

/** Agency-wide numeric caps. Absent on the document = the server's defaults. */
export type CrmOrgSettings = {
  maxExportRows: number;
  maxBulkDelete: number;
};

/** One row of the roster. Admin audience only. */
export type CrmOrgMemberView = {
  /** The member's email — the `dashboard-users-v2` doc id, lowercased. */
  userId: string;
  displayName: string | null;
  role: CrmOrgRole;
  /** ALREADY RESOLVED by the server: the stored entry, or the role default. */
  permissions: CrmMemberPermissions;
  /**
   * True when this member carries an explicit stored entry rather than riding the
   * role default.
   *
   * Note the deliberate consequence: saving permissions that EQUAL the role default
   * DELETES the entry server-side, so `configured` goes back to false. That is what
   * keeps an unconfigured agency storing nothing, and what makes "back to default" a
   * plain save rather than a second endpoint.
   */
  configured: boolean;
};

/**
 * `GET /api/v1/crm/org/overview?orgId=` — ONE endpoint, TWO shapes.
 *
 * A non-admin is answered, not refused: `isAdmin: false`, their own resolved
 * `permissions`, and `members: null`. Every agent is entitled to know what they may
 * do, and a 403 there would leave this screen unable to explain itself.
 */
export type CrmOrgOverviewResponse = {
  orgId: string;
  orgName: string;
  isAdmin: boolean;
  /** The CALLER's own role and permissions. Present for BOTH audiences. */
  role: CrmOrgRole;
  permissions: CrmMemberPermissions;
  settings: CrmOrgSettings;
  /**
   * The role → permissions baseline, SERVED rather than hardcoded in the browser.
   *
   * THE BROWSER HOLDS NO COPY OF THIS, on purpose — one was deleted for exactly this
   * reason. Its single use in the UI is the reset action, which sends the baseline back
   * VERBATIM so the server recognises the result as the default and deletes the stored
   * entry; a drifted local copy would instead write a set the server considers custom,
   * silently converting an unconfigured agency into a configured one.
   *
   * Nothing else needs it: rows are labelled from `configured`, and the controls send
   * ABSOLUTE values, so — unlike the deleted capability model, where a save was a
   * deviation computed against the baseline — a missing `defaults` costs the reset
   * button and nothing more. It cannot corrupt a save.
   */
  defaults: Record<CrmOrgRole, CrmMemberPermissions>;
  /** Admin audience only. `null` — never `[]` — for a plain member. */
  members: CrmOrgMemberView[] | null;
  /**
   * `dashboard-orgs.admins` is empty. Measured 2026-08-16: 27 of 96 orgs.
   *
   * Nobody in such an agency can configure anything — every write 403s — and no
   * bootstrap is offered, here or on the server, because minting an admin from a
   * self-service endpoint is a privilege-escalation primitive. The flag exists so the
   * screen can say so plainly instead of rendering an editor that fails on every save.
   */
  hasNoAdmins: boolean;
};

/**
 * `POST /api/v1/crm/org/settings` — partial; unknown keys are REJECTED, not dropped.
 * `orgId` is optional for the same reason it is on the overview.
 */
export type CrmOrgSettingsUpdateRequest = Partial<CrmOrgSettings> & { orgId?: string };

/**
 * `POST /api/v1/crm/org/members/update` — partial permissions for ONE member.
 *
 * There is deliberately no `role`: the role IS `dashboard-orgs.admins`, and this
 * endpoint does not write the roster. Sending one is rejected as an unknown field
 * rather than ignored, so "I promoted them and nothing happened" cannot occur.
 */
export type CrmOrgMemberUpdateRequest = Partial<CrmMemberPermissions> & {
  orgId?: string;
  userId: string;
};

/**
 * `POST /api/v1/crm/org/members/update-bulk` — the SAME change for SEVERAL members.
 *
 * Two shapes, mutually exclusive (sending both is a 400):
 *  - a PATCH: applied over every target's currently RESOLVED permissions, so
 *    `{ browse: true }` changes nothing else for any of them;
 *  - `reset: true`: every target goes back to its OWN role default (an admin in the
 *    selection to the admin default, a member to the member default) and its stored entry
 *    is deleted — exactly what the single "החזר לברירת המחדל" does, once per target.
 *
 * ALL OR NOTHING: one transaction; one `userId` that is not a current member refuses the
 * whole request with nothing written. At most 200 targets; duplicates collapse server-side.
 */
export type CrmOrgMembersBulkUpdateRequest = Partial<CrmMemberPermissions> & {
  orgId?: string;
  userIds: string[];
  reset?: boolean;
};

/** The resolved rows AFTER the write, in request order. */
export type CrmOrgMembersBulkUpdateResponse = { members: CrmOrgMemberView[] };

/**
 * `POST /api/v1/crm/org/members/role` — make a member an admin, or an admin a member.
 *
 * The ONE endpoint that writes `dashboard-orgs.admins` / `users`. Admin-only. The server
 * refuses to demote the last admin, keeps every admin inside `users[]`, and deletes the
 * member's stored `crmPermissions` entry only if it now equals the new role's default (a
 * differing one survives — an explicit configuration is never undone by a roster change).
 * A repeated request for a role already held is a 200 that writes nothing.
 */
export type CrmOrgMemberRoleUpdateRequest = { orgId?: string; userId: string; role: CrmOrgRole };

// --- Org directory --------------------------------------------------------
//
// Who my colleagues are — name, face, email, admin-or-not. A DIFFERENT SURFACE from the
// overview above, and deliberately not part of it:
//
//   /overview   carries per-member `permissions`, so its roster is ADMIN-ONLY
//               (`members: null` otherwise). It is the control plane.
//   /directory  carries no permission field of any kind and is open to every member.
//               It is presentation: the name and avatar rendered beside a shared note, a
//               contact row's owner, an entry in an owner picker.
//
// It replaces `FirestoreContext.organizationUsers`, which built the same list in the
// browser with one `collectionGroup` query PER MEMBER against `listingData` — a
// subcollection `firestore.rules` opens with `allow read: if true` and which carries
// `phoneNumber`. This is strictly less data, in one round trip, and it fixes the two bugs
// that path cannot: it reads `users ∪ admins` (three of 96 orgs list an admin ONLY under
// `admins`), and it works for an admins-only member, whose browser cannot read their own
// org document at all — so today they see an EMPTY colleague list.

/** One colleague, as every surface needs to render them. */
export type OrgDirectoryMember = {
  /** Lowercased email — the `dashboard-users-v2` doc id, and the join key on every other wire type. */
  userId: string;
  /**
   * `User.ownerFullName`. NULL rather than the email when unresolved: the CALLER owns the
   * fallback, and returning the email here would destroy the ability to tell a real name
   * from a placeholder (which is what decides whether to render initials or a full name).
   */
  displayName: string | null;
  /**
   * `User.photoURL || User.userImageUrl` — the SAME expression the server stamps into
   * `ownersData[0].avatarUrl`, which is where `CrmContactsListRow.ownerAvatarUrl` comes
   * from. One resolution, so a face cannot appear on a contact row and be missing from the
   * member list for the same person.
   *
   * NULL, never `""` — `<Avatar src="">` renders a broken image, whereas `undefined`/absent
   * falls back to the Avatar's children. Pass `avatarUrl ?? undefined` to MUI.
   */
  avatarUrl: string | null;
  /**
   * Derived server-side from `dashboard-orgs.admins`. There is deliberately no companion
   * `role` string: two vocabularies for one fact is how they drift. Across a multi-org
   * union it means "admin of at least one org shared with the caller"; with a single org
   * (the overwhelming case) it is exact.
   */
  isAdmin: boolean;
};

/**
 * `GET /api/v1/crm/org/directory?orgId=` — NEVER 403, NEVER 400.
 *
 * A caller in no organization gets a one-row directory containing themselves; a caller in
 * several gets the deduped UNION plus `orgs`, and may narrow with `orgId`. Both differ
 * from the overview on purpose: that endpoint refuses to guess between agencies because
 * its payload is per-org AUTHORITY, whereas a directory has no per-org redaction to get
 * wrong. And "no org" is the fleet majority — 287 users are in any org, across 96 — so on
 * a response the app preloads at startup it has to be a usable body rather than an error
 * every consumer classifies.
 */
export type OrgDirectoryResponse = {
  /** The single org this answer is about; `null` when it spans several orgs — or none. */
  orgId: string | null;
  orgName: string | null;
  /** ALWAYS contains the caller. That is what gives consumers exactly one code path. */
  members: OrgDirectoryMember[];
  /**
   * Every org the caller belongs to. Not derivable client-side — membership is resolved
   * server-side.
   *
   * `memberCount` is that org's `users ∪ admins`, caller included — PER ORG, because
   * `members` above is the deduped union for a multi-agency caller and its length would
   * label one agency with another's roster. It is a display label, never an authorization
   * input, and it rides the directory precisely so the CRM's agency tab can say how many
   * agents it covers on the FIRST paint (the directory is persisted; see
   * `utils/persistedCache.ts`).
   */
  orgs: { id: string; name: string | null; isAdmin: boolean; memberCount: number }[];
};
